import { renderToStaticMarkup } from 'react-dom/server';
import { Behavior } from './Behavior';
import questionService from '../services/QuestionService';
import UtilsService from '../services/UtilsService';
import { ContentType, LessonMode, PartialScoringType, QuestionFeedbackState } from '../../Constants';
import HtmlComponent from '../components/HtmlComponent';
import questionFeedbackManager from '../managers/QuestionFeedbackManager';

export class ImageLabelMultichoiceBehavior extends Behavior {
  checkForValidResponse = (response, model) => {
    // Can also return a object with optional keys `isValid`, `title`, `text`, and `allowSubmit` to control the dialog box
    // Valid if all prompts have text that is not an empty string
    const valid = response.prompts.every(({ text }) => text.length > 0);
    return valid;
  }

  isAutoScored = (model) => {
    let autoScore = ContentType.getAutoscore(model.type) && !model.survey && !model.unscored;
    if (model.responseFormat && model.responseFormat === 'html') {
      autoScore = false;
    }
    return autoScore;
  }

  setCorrectAnswer = (state, model) => {
    const temp = model.validation?.prompts.map(({ id, correctAnswers }) => ({ id, text: correctAnswers[0] }));
    if (!temp) {
      // console.debug('ImageLabelMultichoiceBehavior.setCorrectAnswer: no correct answers', model);
      return;
    }
    state.setCurrentResponse(UtilsService.safeMobxClone({ ...state.cachedResponseAnswer, prompts: temp }));
  }

  resetStudentAnswer = (state) => {
    state.setCurrentResponse(UtilsService.safeMobxClone(state.cachedResponseAnswer));
  }

  setResponseData = ({ id, text }, responseAnswer) => {
    const currentAnswer = responseAnswer.prompts.find(({ id: promptId }) => promptId === id);
    if (!currentAnswer) {
      // console.debug('Tried to set ImageLabelMultichoice response data for prompt that does not exist', id, text);
      return;
    }
    currentAnswer.text = text;
  }

  getAnswerFeedback = (id, responseAnswer) => {
    return true;
  }

  getInitializedResponse = (lessonElementId, model, activityId) => {
    const prompts = model.prompts.map(({ id }) => ({ id, text: '' }));
    const responseAnswer = {
      ...questionService.generateInitializedQuestionResponse({
        lessonElementId,
        prompts
      })
    };
    return responseAnswer;
  }

  getCorrectAnswersText = (model) => {
    const answerTexts = model.validation?.prompts.map(({ id, correctAnswers }, index) => ({ id, index, text: correctAnswers.join(', ') }));

    return renderToStaticMarkup(
      <>
        {
          answerTexts.map(({ id, index, text }) => {
            return (
              <div key={id} style={{ display: 'flex', alignItems: 'center' }}>
                {index + 1}:&nbsp;<HtmlComponent htmlStr={text} /><br />
              </div>
            );
          })
        }
      </>
    );
  }

  getQuestionFeedbackText = (model, correctFeedback, incorrectFeedback, correctFeedbackBody, incorrectFeedbackBody, mode, questionIsCorrect, currentAttemptCount, { lessonElementState }) => {
    let textBody = '';
    if (mode === LessonMode.PREVIEW || mode === LessonMode.ITEM_PREVIEW || mode === LessonMode.STUDENT_PREVIEW || mode === LessonMode.PUBLISHER_PREVIEW) {
      textBody += UtilsService.previewWarning();
    } else {
      if (typeof currentAttemptCount !== 'undefined' && currentAttemptCount !== -1 && currentAttemptCount > 1) {
        textBody += UtilsService.saveWarning();
      } else {
        textBody += UtilsService.saveCommit();
      }
    }

    let textHeader = '';
    let attemptsText = '';
    
    // Add attempts remaining text for practice mode when answer is incorrect or partially correct
    if (questionFeedbackManager.isPractice && 
        (questionIsCorrect === QuestionFeedbackState.INCORRECT || questionIsCorrect === QuestionFeedbackState.PARTIAL_CORRECT) &&
        model.lessonElementId) {
      attemptsText = questionFeedbackManager.getAttemptsRemainingText(model.lessonElementId);
    }

    if (questionIsCorrect === QuestionFeedbackState.CORRECT) {
      textHeader = correctFeedback;
      if (correctFeedbackBody) {
        textBody = `${correctFeedbackBody} ${textBody}`;
      }
    } else if (questionIsCorrect === QuestionFeedbackState.PARTIAL_CORRECT) {
      textHeader = `${UtilsService.partiallyCorrect()}`;
      let custom = '';
      if (incorrectFeedbackBody) {
        custom = `${incorrectFeedbackBody } `;
      }

      textBody = `${UtilsService.partiallyCorrectPrompt()}. <br/>${ custom }${textBody}`;
    } else {
      textHeader = incorrectFeedback;
      if (incorrectFeedbackBody) {
        textBody = `${incorrectFeedbackBody} ${textBody}`;
      }
    }

    // Add attempts text after the header if it exists
    if (attemptsText) {
      textHeader += attemptsText;
    }

    let finalText = '<div class=\'question-feedback-header\'>';
    finalText += textHeader;
    finalText += '</div>';
    finalText += '<div class=\'question-feedback-body\'>';
    finalText += textBody;
    finalText += '</div>';

    return finalText;
  }

  isAnswerCorrect = (answerId, lessonElementState, model) => {
    // Checks if the current answer is in the list of correct answers
    const responseAnswer = lessonElementState.currentResponseAnswer;
    const currentAnswerText = responseAnswer.prompts.find(({ id: promptId }) => promptId === answerId)?.text;
    const validationAnswerTexts = model.validation?.prompts.find(({ id: promptId }) => promptId === answerId)?.correctAnswers;
    if (!currentAnswerText || !validationAnswerTexts ) {
      // console.debug('ImageLabelMultichoiceBehavior.isAnswerCorrect: no answer or validation answer found', answerId, currentAnswerText, validationAnswerTexts);
      return false;
    }
    return validationAnswerTexts.includes(currentAnswerText);
  }

  isQuestionCorrect = (lessonElementState, lessonElementId) => {
    if (!lessonElementState) {
      return QuestionFeedbackState.INCORRECT;
    }

    if (lessonElementState.submittedItemScore >= lessonElementState.maxScore) {
      return QuestionFeedbackState.CORRECT;
    }
    if ((lessonElementState.submittedItemScore > 0) && (lessonElementState.submittedItemScore < lessonElementState.maxScore)) {
      return QuestionFeedbackState.PARTIAL_CORRECT;
    }

    return QuestionFeedbackState.INCORRECT;
  }
}

export default new ImageLabelMultichoiceBehavior();
